import React, { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { getMarketCoverage } from '../lib/api';
import { formatNumber, formatPercent } from '../lib/utils';
import { Skeleton } from '../components/ui/skeleton';
import { TrafficLight } from '../components/TrafficLight';
import { DataLoader } from '../components/ForecastLoader';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { PieChart, Target, TrendingUp, ArrowUpRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const CoberturaMercadoPage = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('untapped');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await getMarketCoverage();
        setData(response.data);
      } catch (error) {
        console.error('Error:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <Layout>
        <div className="space-y-6">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Cobertura de Mercado</h1>
            <p className="text-slate-500 mt-1">Cargando análisis...</p>
          </div>
          <DataLoader type="coverage" />
        </div>
      </Layout>
    );
  }

  // Sort clients
  const sortedClients = [...(data?.clients || [])].sort((a, b) => {
    switch (sortBy) {
      case 'untapped':
        return b.untapped_potential - a.untapped_potential;
      case 'share':
        return b.share_percentage - a.share_percentage;
      case 'volume':
        return b.our_volume - a.our_volume;
      default:
        return 0;
    }
  });

  const totals = data?.totals || {};

  return (
    <Layout>
      <div className="space-y-8" data-testid="cobertura-mercado-page">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Cobertura de Mercado</h1>
            <p className="text-slate-500 mt-1">Análisis de share of wallet y potencial de captura</p>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="bg-indigo-600 rounded-xl p-6 text-white">
            <div className="flex items-center gap-2 mb-3">
              <PieChart className="w-5 h-5 text-indigo-200" />
              <span className="text-sm font-medium text-indigo-200">Share Total</span>
            </div>
            <p className="text-4xl font-bold">{formatPercent(totals.share_percentage, 0)}</p>
            <p className="text-sm text-indigo-200 mt-1">del mercado capturado</p>
          </div>
          
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <div className="flex items-center gap-2 mb-3">
              <Target className="w-5 h-5 text-slate-400" />
              <span className="text-sm font-medium text-slate-500">Nuestro Volumen</span>
            </div>
            <p className="text-3xl font-bold text-slate-900">{formatNumber(totals.our_volume)}</p>
            <p className="text-sm text-slate-500 mt-1">kgs mensuales</p>
          </div>
          
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <div className="flex items-center gap-2 mb-3">
              <PieChart className="w-5 h-5 text-slate-400" />
              <span className="text-sm font-medium text-slate-500">Mercado Total</span>
            </div>
            <p className="text-3xl font-bold text-slate-900">{formatNumber(totals.market_total)}</p>
            <p className="text-sm text-slate-500 mt-1">kgs mensuales (Penta)</p>
          </div>
          
          <div className="bg-emerald-50 rounded-xl border border-emerald-200 p-6">
            <div className="flex items-center gap-2 mb-3">
              <TrendingUp className="w-5 h-5 text-emerald-600" />
              <span className="text-sm font-medium text-emerald-700">Potencial</span>
            </div>
            <p className="text-3xl font-bold text-emerald-700">{formatNumber(totals.untapped_potential)}</p>
            <p className="text-sm text-emerald-600 mt-1">kgs disponibles</p>
          </div>
        </div>

        {/* Top 5 Capture Opportunities */}
        <div className="bg-gradient-to-r from-indigo-500 to-violet-600 rounded-2xl p-6 text-white shadow-lg shadow-indigo-500/20">
          <h2 className="text-lg font-semibold mb-4">Top 5 - Mayor Potencial de Captura Inmediata</h2>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            {data?.top_5_capture?.map((client, i) => (
              <div 
                key={client.client_id}
                onClick={() => navigate(`/clientes/${client.client_id}`)}
                className="bg-white/10 backdrop-blur rounded-xl p-4 cursor-pointer hover:bg-white/20 transition-colors"
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-2xl font-bold text-white/50">#{i + 1}</span>
                  <ArrowUpRight className="w-4 h-4 text-white/50" />
                </div>
                <p className="font-medium text-white truncate">{client.client_name}</p>
                <p className="text-2xl font-bold mt-2">{formatNumber(client.untapped_potential)}</p>
                <p className="text-xs text-white/70">kgs potenciales</p>
              </div>
            ))}
          </div>
        </div>

        {/* Main Table */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
          <div className="p-5 border-b border-slate-100 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-slate-900">Detalle por Cliente</h2>
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-[180px]" data-testid="sort-coverage-select">
                <SelectValue placeholder="Ordenar por" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="untapped">Mayor potencial</SelectItem>
                <SelectItem value="share">Mayor share</SelectItem>
                <SelectItem value="volume">Mayor volumen</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider px-5 py-3">Cliente</th>
                  <th className="text-right text-xs font-medium text-slate-500 uppercase tracking-wider px-5 py-3">Nuestro Vol.</th>
                  <th className="text-right text-xs font-medium text-slate-500 uppercase tracking-wider px-5 py-3">Mercado Total</th>
                  <th className="text-center text-xs font-medium text-slate-500 uppercase tracking-wider px-5 py-3">Share</th>
                  <th className="text-right text-xs font-medium text-slate-500 uppercase tracking-wider px-5 py-3">Potencial</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {sortedClients.map((client) => (
                  <tr 
                    key={client.client_id} 
                    className="hover:bg-slate-50 cursor-pointer transition-colors"
                    onClick={() => navigate(`/clientes/${client.client_id}`)}
                    data-testid={`coverage-row-${client.client_id}`}
                  >
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-3">
                        <TrafficLight status={client.status} size="sm" />
                        <div>
                          <p className="font-medium text-slate-900">{client.client_name}</p>
                          <p className="text-xs text-slate-500">{client.executive_name}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-4 text-right">
                      <span className="font-semibold text-indigo-600">{formatNumber(client.our_volume)}</span>
                      <span className="text-xs text-slate-400 ml-1">kgs</span>
                    </td>
                    <td className="px-5 py-4 text-right">
                      <span className="font-medium text-slate-900">{formatNumber(client.market_total)}</span>
                      <span className="text-xs text-slate-400 ml-1">kgs</span>
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex flex-col items-center">
                        <span className="font-medium text-slate-900 mb-1">{formatPercent(client.share_percentage, 0)}</span>
                        <div className="w-24 h-2 bg-slate-100 rounded-full overflow-hidden">
                          <div className="h-full flex">
                            <div 
                              className="bg-indigo-500 h-full"
                              style={{ width: `${Math.min(client.share_percentage, 100)}%` }}
                            />
                            <div 
                              className="bg-slate-200 h-full"
                              style={{ width: `${100 - Math.min(client.share_percentage, 100)}%` }}
                            />
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-4 text-right">
                      <span className="font-semibold text-emerald-600">+{formatNumber(client.untapped_potential)}</span>
                      <span className="text-xs text-slate-400 ml-1">kgs</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default CoberturaMercadoPage;
